/**
 * Dashboard service for client-side API calls
 */
import apiService from './apiService';
import fallbackService from './fallbackService';

// Flag to control whether to use fallback service
const USE_FALLBACK = false;

// Lightweight in-memory cache for expensive dashboard usage calls
const usageCache = {
  today: {},
  yesterday: {},
  last30days: {}
};

// Cache expiration time in milliseconds (5 minutes)
const CACHE_EXPIRATION = 5 * 60 * 1000;

const isCacheValid = (cache) => {
  return cache && cache.timestamp && (Date.now() - cache.timestamp < CACHE_EXPIRATION);
};

const dashboardService = {
  /**
   * Get dashboard data
   * @param {string|number} siteId - Site ID (use 'all' for all sites)
   * @returns {Promise<Object>} - Dashboard data
   */
  async getDashboardData(siteId = 'all') {
    try {
      if (USE_FALLBACK) {
        console.log('Using fallback dashboard service');
        return fallbackService.dashboard.getDashboardData(siteId);
      }
      
      const endpoint = siteId === 'all' 
        ? '/dashboard' 
        : `/dashboard?siteId=${siteId}`;
      
      return await apiService.get(endpoint);
    } catch (error) {
      console.error('Get dashboard data error:', error);
      // Do not fall back to mock data
      throw error;
    }
  },

  /**
   * Get today's usage data
   * @param {string|number} siteId - Site ID (use 'all' for all sites)
   * @returns {Promise<Object>} - Today's usage data
   */
  async fetchTodayUsage(siteId = 'all') {
    try {
      if (USE_FALLBACK) {
        console.log('Using fallback today usage service');
        return fallbackService.dashboard.getTodayUsage(siteId);
      }

      const cacheKey = String(siteId);
      if (isCacheValid(usageCache.today[cacheKey])) {
        return usageCache.today[cacheKey].data;
      }
      
      const endpoint = siteId === 'all' 
        ? '/dashboard/today' 
        : `/dashboard/today?siteId=${siteId}`;
      
      const data = await apiService.get(endpoint);
      usageCache.today[cacheKey] = { data, timestamp: Date.now() };
      return data;
    } catch (error) {
      console.error('Get today usage error:', error);
      throw error;
    }
  },

  /**
   * Get yesterday's usage data
   * @param {string|number} siteId - Site ID (use 'all' for all sites)
   * @returns {Promise<Object>} - Yesterday's usage data
   */
  async fetchYesterdayUsage(siteId = 'all') {
    try {
      if (USE_FALLBACK) {
        console.log('Using fallback yesterday usage service');
        return fallbackService.dashboard.getYesterdayUsage(siteId);
      }

      const cacheKey = String(siteId);
      if (isCacheValid(usageCache.yesterday[cacheKey])) {
        return usageCache.yesterday[cacheKey].data;
      }
      
      const endpoint = siteId === 'all' 
        ? '/dashboard/yesterday' 
        : `/dashboard/yesterday?siteId=${siteId}`;
      
      const data = await apiService.get(endpoint);
      usageCache.yesterday[cacheKey] = { data, timestamp: Date.now() };
      return data;
    } catch (error) {
      console.error('Get yesterday usage error:', error);
      throw error;
    }
  },

  /**
   * Get last 30 days total usage
   * @param {string|number} siteId - Site ID (use 'all' for all sites)
   * @returns {Promise<Object>} - Last 30 days usage data
   */
  async fetchLast30DaysUsage(siteId = 'all') {
    try {
      if (USE_FALLBACK) {
        console.log('Using fallback 30 days usage service');
        return fallbackService.dashboard.getLast30DaysUsage(siteId);
      }

      const cacheKey = String(siteId);
      if (isCacheValid(usageCache.last30days[cacheKey])) {
        return usageCache.last30days[cacheKey].data;
      }
      
      const endpoint = siteId === 'all' 
        ? '/dashboard/last30days' 
        : `/dashboard/last30days?siteId=${siteId}`;
      
      const data = await apiService.get(endpoint);
      usageCache.last30days[cacheKey] = { data, timestamp: Date.now() };
      return data;
    } catch (error) {
      console.error('Get last 30 days usage error:', error);
      throw error;
    }
  },

  /**
   * Get daily usage data for the last N days
   * @param {number} days - Number of days
   * @param {string|number} siteId - Site ID (use 'all' for all sites)
   * @returns {Promise<Array>} - Daily usage data
   */
  async getDailyUsage(days = 30, siteId = 'all') {
    try {
      if (USE_FALLBACK) {
        console.log('Using fallback daily usage service');
        return fallbackService.dashboard.getDailyUsage(days, siteId);
      }
      
      const endpoint = siteId === 'all' 
        ? `/dashboard/daily?days=${days}` 
        : `/dashboard/daily?days=${days}&siteId=${siteId}`;
      
      return await apiService.get(endpoint);
    } catch (error) {
      console.error('Get daily usage error:', error);
      // Do not fall back to mock data
      throw error;
    }
  },

  /**
   * Get monthly usage data for the last N months
   * @param {number} months - Number of months
   * @param {string|number} siteId - Site ID (use 'all' for all sites)
   * @returns {Promise<Array>} - Monthly usage data
   */
  async getMonthlyUsage(months = 12, siteId = 'all') {
    try {
      if (USE_FALLBACK) {
        console.log('Using fallback monthly usage service');
        return fallbackService.dashboard.getMonthlyUsage(months, siteId);
      }
      
      const endpoint = siteId === 'all' 
        ? `/dashboard/monthly?months=${months}` 
        : `/dashboard/monthly?months=${months}&siteId=${siteId}`;
      
      return await apiService.get(endpoint);
    } catch (error) {
      console.error('Get monthly usage error:', error);
      // Do not fall back to mock data
      throw error;
    }
  },

  /**
   * Get hourly usage data for today
   * @param {string|number} siteId - Site ID (use 'all' for all sites)
   * @returns {Promise<Array>} - Hourly usage data
   */
  async getTodayHourlyUsage(siteId = 'all') {
    try {
      if (USE_FALLBACK) {
        return fallbackService.dashboard.getDashboardData(siteId).todayHourlyData;
      }
      
      const endpoint = siteId === 'all' 
        ? '/dashboard/today-hourly' 
        : `/dashboard/today-hourly?siteId=${siteId}`;
      
      return await apiService.get(endpoint);
    } catch (error) {
      console.error('Get today hourly usage error:', error);
      // Do not fall back to mock data
      throw error;
    }
  },

  /**
   * Get hourly usage data for yesterday
   * @param {string|number} siteId - Site ID (use 'all' for all sites)
   * @returns {Promise<Array>} - Hourly usage data
   */
  async getYesterdayHourlyUsage(siteId = 'all') {
    try {
      if (USE_FALLBACK) {
        return fallbackService.dashboard.getDashboardData(siteId).yesterdayHourlyData;
      }
      
      const endpoint = siteId === 'all' 
        ? '/dashboard/yesterday-hourly' 
        : `/dashboard/yesterday-hourly?siteId=${siteId}`;
      
      return await apiService.get(endpoint);
    } catch (error) {
      console.error('Get yesterday hourly usage error:', error);
      // Do not fall back to mock data
      throw error;
    }
  },

  /**
   * Get device by ID
   * @param {string|number} id - Device ID
   * @returns {Promise<Object>} - Device data
   */
  async fetchDevice(id) {
    try {
      if (USE_FALLBACK) {
        console.log('Using fallback device service');
        return fallbackService.devices.getDeviceById(id);
      }
      
      return await apiService.get(`/devices/${id}`);
    } catch (error) {
      console.error(`Get device ${id} error:`, error);
      throw error;
    }
  }
};

export default dashboardService;
